import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MessageCircle, ChevronLeft, ChevronDown, XCircle,
  Mail, KeyRound, Package, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { fmtBRL, buildWhatsAppUrl } from "@/lib/format";
import { imgVariant } from "@/lib/img";
import { validCPF } from "@/pages/CheckoutPage";
import { OrderTimeline } from "@/components/store/OrderTimeline";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const onlyDigits = (v) => (v || "").replace(/\D/g, "");
const maskCPF = (v) =>
  onlyDigits(v).slice(0, 11).replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d)/, "$1.$2").replace(/(\d{3})(\d{1,2})$/, "$1-$2");
const fmtDate = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");
const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
};

const STATUS_PILL = {
  aguardando_confirmacao: ["Aguardando confirmação", "bg-amber-50 text-amber-700"],
  confirmado: ["Confirmado", "bg-blue-50 text-brand"],
  em_separacao: ["Em separação", "bg-violet-50 text-violet-700"],
  saiu_para_entrega: ["Saiu para entrega", "bg-indigo-50 text-indigo-700"],
  entregue: ["Entregue", "bg-green-50 text-green-700"],
  cancelamento_solicitado: ["Cancelamento em análise", "bg-orange-50 text-orange-700"],
  cancelado: ["Cancelado", "bg-red-50 text-red-600"],
};

export default function MyOrdersPage() {
  const settings = useSettings();
  const [stage, setStage] = useState("cpf"); // cpf | code | list
  const [cpf, setCpf] = useState("");
  const [code, setCode] = useState("");
  const [token, setToken] = useState(sessionStorage.getItem("hg_cpf_session") || "");
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(false);

  const waHelp = buildWhatsAppUrl(
    "Olá! Não consigo acessar o e-mail/telefone cadastrado e preciso verificar meus pedidos. Podem me ajudar?",
    settings.whatsapp_number
  );

  const requestCode = async (e) => {
    e.preventDefault();
    if (!validCPF(cpf)) {
      toast.error("CPF inválido. Confira os números digitados.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/by-cpf/request", { cpf: onlyDigits(cpf) });
      toast.success(data.detail, { duration: 8000 });
      setStage("code");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível enviar o código."));
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/orders/by-cpf/verify", { cpf: onlyDigits(cpf), code: onlyDigits(code) });
      sessionStorage.setItem("hg_cpf_session", data.token);
      setToken(data.token);
      await loadOrders(data.token);
      setStage("list");
    } catch (err) {
      toast.error(formatApiError(err, "Código inválido ou expirado."));
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async (tk = token) => {
    try {
      const { data } = await api.get("/orders/by-cpf", {
        headers: { Authorization: `Bearer ${tk}` },
      });
      setOrders(data);
      return true;
    } catch {
      sessionStorage.removeItem("hg_cpf_session");
      setToken("");
      setStage("cpf");
      toast.error("Sessão expirada. Solicite um novo código.");
      return false;
    }
  };

  // sessão existente: entra direto na lista
  useEffect(() => {
    if (token && stage === "cpf") {
      setStage("list");
      loadOrders(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doCancel = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.post(
        "/orders/by-cpf/cancel",
        { order_id: cancelTarget.id, reason: cancelReason.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        cancelTarget.status === "aguardando_confirmacao"
          ? "Pedido cancelado com sucesso."
          : "Solicitação de cancelamento registrada! A equipe vai analisar."
      );
      setCancelTarget(null);
      setCancelReason("");
      loadOrders();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível registrar o cancelamento."));
    } finally {
      setCancelling(false);
    }
  };

  const leave = () => {
    sessionStorage.removeItem("hg_cpf_session");
    setToken("");
    setOrders(null);
    setStage("cpf");
    setCpf("");
    setCode("");
  };

  return (
    <div className="container-store py-8 sm:py-12 max-w-2xl mx-auto" data-testid="myorders-page">
      <Seo title="Meus Pedidos | HG Móveis" noindex />
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Meus Pedidos</h1>
      <p className="text-sm text-gray-500 mt-1">
        Consulte todos os seus pedidos com segurança: pedimos uma prova de identidade antes de mostrar qualquer dado.
      </p>

      {/* ETAPA: CPF */}
      {stage === "cpf" && (
        <form onSubmit={requestCode} className="mt-6 bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="myorders-cpf-step">
          <Label htmlFor="myorders-cpf">Digite seu CPF</Label>
          <div className="mt-1.5 flex gap-2">
            <Input
              id="myorders-cpf"
              inputMode="numeric"
              value={cpf}
              onChange={(e) => setCpf(maskCPF(e.target.value))}
              placeholder="000.000.000-00"
              className="flex-1"
              data-testid="myorders-cpf"
            />
            <Button type="submit" disabled={loading} className="rounded-full" data-testid="myorders-request">
              <Mail className="w-4 h-4" /> {loading ? "Enviando..." : "Receber código"}
            </Button>
          </div>
          <p className="mt-3 text-xs text-gray-500 flex items-start gap-1.5 leading-relaxed">
            <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-green-600 shrink-0" />
            Por segurança, enviamos um código temporário para o e-mail cadastrado no pedido. Nenhum dado é exibido
            antes da confirmação — assim ninguém consulta pedidos de outra pessoa.
          </p>
          <a href={waHelp} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs text-green-700 font-medium hover:underline" data-testid="myorders-help-whatsapp">
            <MessageCircle className="w-3.5 h-3.5" /> Não tenho mais acesso ao e-mail/telefone cadastrado
          </a>
        </form>
      )}

      {/* ETAPA: código */}
      {stage === "code" && (
        <form onSubmit={verifyCode} className="mt-6 bg-white border border-gray-100 rounded-2xl p-5 sm:p-6" data-testid="myorders-code-step">
          <Label htmlFor="myorders-code">Código de verificação</Label>
          <p className="text-xs text-gray-500 mt-0.5">
            Se houver pedidos com e-mail cadastrado para o CPF {cpf}, o código foi enviado (vale por 10 minutos).
          </p>
          <div className="mt-3 flex gap-2">
            <Input
              id="myorders-code"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(onlyDigits(e.target.value).slice(0, 6))}
              placeholder="6 dígitos"
              className="flex-1 text-center text-lg tracking-[0.4em] font-bold"
              data-testid="myorders-code"
            />
            <Button type="submit" disabled={loading || code.length !== 6} className="rounded-full" data-testid="myorders-verify">
              <KeyRound className="w-4 h-4" /> {loading ? "Verificando..." : "Confirmar"}
            </Button>
          </div>
          <button type="button" onClick={() => setStage("cpf")} className="mt-3 text-xs text-brand font-medium hover:underline" data-testid="myorders-new-code">
            Não recebi — solicitar outro código
          </button>
        </form>
      )}

      {/* ETAPA: lista de pedidos */}
      {stage === "list" && orders && (
        <div className="mt-6 space-y-4" data-testid="myorders-list">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              <strong>{orders.length}</strong> pedido(s) encontrado(s)
            </p>
            <button onClick={leave} className="text-xs text-gray-400 hover:text-red-500 font-medium" data-testid="myorders-logout">
              Sair da consulta
            </button>
          </div>

          {orders.length === 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-10 text-center text-gray-400">
              <Package className="w-10 h-10 mx-auto text-gray-200" />
              <p className="mt-2 text-sm">Nenhum pedido encontrado para este CPF.</p>
              <Link to="/produtos">
                <Button className="mt-4 rounded-full" data-testid="myorders-shop-empty">Conhecer ofertas</Button>
              </Link>
            </div>
          )}

          {orders.map((o) => {
            const pill = STATUS_PILL[o.status] || [o.status_label, "bg-gray-100 text-gray-600"];
            const open = openId === (o.id || o.order_number);
            const canCancel = ["aguardando_confirmacao", "confirmado", "em_separacao", "saiu_para_entrega"].includes(o.status);
            return (
              <div key={o.order_number} className="bg-white border border-gray-100 rounded-2xl overflow-hidden" data-testid={`myorder-card-${o.order_number}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : o.id || o.order_number)}
                  className="w-full p-4 sm:p-5 flex items-center gap-4 text-left hover:bg-gray-50/60 transition-colors"
                  data-testid={`myorder-open-${o.order_number}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-bold text-gray-900">{o.order_number}</span>
                      <span className={`text-[10px] font-bold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${pill[1]}`} data-testid={`myorder-status-${o.order_number}`}>
                        {pill[0]}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {fmtDateTime(o.created_at)} • {o.items.reduce((s, i) => s + (i.qty || 1), 0)} item(ns) • {fmtBRL(o.total)}
                    </p>
                  </div>
                  {o.items[0]?.image && (
                    <img src={imgVariant(o.items[0].image, "thumb")} alt="" loading="lazy" decoding="async" className="w-12 h-12 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />
                  )}
                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>

                {open && (
                  <div className="px-4 sm:px-5 pb-5 border-t border-gray-100" data-testid={`myorder-detail-${o.order_number}`}>
                    {/* linha do tempo */}
                    {!["cancelado", "cancelamento_solicitado"].includes(o.status) ? (
                      <div className="mt-5">
                        <OrderTimeline status={o.status} />
                      </div>
                    ) : (
                      <div className={`mt-4 rounded-xl p-3.5 text-sm flex items-start gap-2 ${o.status === "cancelado" ? "bg-red-50 text-red-700" : "bg-orange-50 text-orange-800"}`}>
                        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>
                          {o.status === "cancelado"
                            ? "Este pedido foi cancelado."
                            : "Sua solicitação de cancelamento está em análise pela equipe."}
                        </span>
                      </div>
                    )}

                    {/* itens */}
                    <ul className="mt-5 space-y-3">
                      {o.items.map((i, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-sm">
                          {i.image && (
                            <img src={imgVariant(i.image, "thumb")} alt="" loading="lazy" decoding="async" className="w-11 h-11 rounded-lg object-contain bg-white border border-gray-100 shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-medium line-clamp-1">{i.name}</p>
                            <p className="text-xs text-gray-500">{i.qty}x{i.color ? ` • Cor: ${i.color}` : ""}</p>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className="mt-4 pt-3 border-t border-gray-100 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-600"><span>Total</span><span className="font-bold text-gray-900">{fmtBRL(o.total)}</span></div>
                      <p className="text-xs text-gray-500">
                        {o.payment_label}{o.installments > 1 ? ` em ${o.installments}x` : ""} • Entrega desejada: {fmtDate(o.delivery_date)}
                      </p>
                      <p className="text-xs text-gray-500">{o.delivery_address}</p>
                    </div>

                    {/* histórico */}
                    {o.status_history?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1.5">Etapas do pedido</p>
                        <ul className="space-y-1">
                          {o.status_history.map((h, i) => (
                            <li key={i} className="text-xs text-gray-500 flex justify-between gap-3">
                              <span>{STATUS_PILL[h.status]?.[0] || h.status}</span>
                              <span className="shrink-0">{fmtDateTime(h.at)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* ações */}
                    <div className="mt-5 flex flex-col sm:flex-row gap-2">
                      {canCancel && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-full border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => setCancelTarget(o)}
                          data-testid={`myorder-cancel-${o.order_number}`}
                        >
                          <XCircle className="w-4 h-4" />
                          {o.status === "aguardando_confirmacao" ? "Cancelar pedido" : "Solicitar cancelamento"}
                        </Button>
                      )}
                      <a
                        href={buildWhatsAppUrl(`Olá! Gostaria de falar sobre meu pedido ${o.order_number}.`, settings.whatsapp_number)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 w-full sm:w-auto" data-testid={`myorder-whatsapp-${o.order_number}`}>
                          <MessageCircle className="w-4 h-4" /> Falar com a HG Móveis
                        </Button>
                      </a>
                      <Link to="/produtos">
                        <Button variant="outline" size="sm" className="rounded-full w-full sm:w-auto" data-testid={`myorder-shop-${o.order_number}`}>
                          <ChevronLeft className="w-4 h-4" /> Voltar às compras
                        </Button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* diálogo de cancelamento */}
      <Dialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <DialogContent className="max-w-md" data-testid="myorder-cancel-dialog">
          <DialogHeader>
            <DialogTitle>
              {cancelTarget?.status === "aguardando_confirmacao" ? "Cancelar pedido" : "Solicitar cancelamento"}
            </DialogTitle>
          </DialogHeader>
          {cancelTarget && (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="font-semibold text-gray-900">{cancelTarget.order_number}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {cancelTarget.items.map((i) => `${i.qty}x ${i.name}`).join(", ")} — Total {fmtBRL(cancelTarget.total)}
                </p>
              </div>
              <div>
                <Label htmlFor="myorder-cancel-reason">Motivo (opcional)</Label>
                <Textarea
                  id="myorder-cancel-reason"
                  rows={3}
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="mt-1.5"
                  data-testid="myorder-cancel-reason"
                />
              </div>
              <p className="text-xs text-gray-500">
                {cancelTarget.status === "aguardando_confirmacao"
                  ? "O pedido será cancelado imediatamente. Essa ação não pode ser desfeita."
                  : "A equipe analisará sua solicitação. Compras online têm direito de arrependimento em até 7 dias após o recebimento (CDC, art. 49)."}
              </p>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setCancelTarget(null)} data-testid="myorder-cancel-abort">
                  Voltar
                </Button>
                <Button onClick={doCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700" data-testid="myorder-cancel-confirm">
                  {cancelling ? "Enviando..." : "Confirmar"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
